import { useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, Star } from "lucide-react";
import { MaskedLine, Reveal, Overline } from "@/components/Motion";
import Ribbon from "@/components/Ribbon";
import { IMAGES, AVIS, SCORES_PLANITY, PLANITY_URL, GOOGLE_REVIEWS_URL } from "@/data/maison";

const STATS = [
  { value: "500+", label: "sourcils et visages sublimés" },
  { value: "5,0", label: "note moyenne — 88 avis Planity" },
  { value: "5 ans", label: "d'expertise en dermopigmentation" },
  { value: "100 %", label: "pigments certifiés REACH" },
];

export default function Home() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);

  const marqueeItems = [
    ...AVIS.map((avis) => ({ type: "avis", ...avis })),
    ...SCORES_PLANITY.map((s) => ({ type: "score", ...s })),
  ];
  const marqueeLoop = [...marqueeItems, ...marqueeItems];

  return (
    <main className="pt-20">
      {/* ── Hero ─────────────────────────────────────────── */}
      <section ref={heroRef} className="relative overflow-hidden" data-testid="home-hero">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 pt-14 md:pt-24 pb-20 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 relative z-10">
            <Reveal y={16}>
              <Overline>Studio privé de dermopigmentation — Paris 17ᵉ</Overline>
            </Reveal>
            <h1 className="mt-8 font-display font-light tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem]">
              <MaskedLine delay={0.15}>Le sourcil est</MaskedLine>
              <MaskedLine delay={0.3}>
                une{" "}
                <span className="font-script text-[1.05em] text-primary">signature</span>.
              </MaskedLine>
            </h1>
            <Reveal delay={0.55} className="mt-8 max-w-md">
              <p className="text-base md:text-lg font-light leading-relaxed text-muted-foreground">
                Maquillage permanent des sourcils, des lèvres et du visage,
                tricopigmentation et soins experts de la peau : bienvenue dans
                le studio privé d'Oriane, à Paris 17ᵉ.
              </p>
            </Reveal>
            <Reveal delay={0.7} className="mt-10 flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-reserver-btn"
                onClick={() => navigate("/studio#reservation")}
                className="bg-primary text-primary-foreground text-sm tracking-[0.14em] uppercase px-8 py-4 transition-colors duration-300 hover:bg-foreground"
              >
                Réserver au studio
              </button>
              <Link
                data-testid="hero-academie-btn"
                to="/academie"
                className="group text-sm tracking-[0.14em] uppercase px-2 py-4 flex items-center gap-2 transition-colors duration-300 hover:text-muted-foreground"
              >
                Découvrir l'académie
                <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>

          <div className="lg:col-span-5 relative">
            <Reveal delay={0.4} y={60}>
              <div className="relative">
                <div className="overflow-hidden rounded-t-[999px] border border-border aspect-[3/4]">
                  <motion.img
                    src={IMAGES.portraitR}
                    alt="Signature Maison Ria — regard sublimé"
                    style={{ y: imgY, scale: 1.12 }}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -left-6 bottom-10 bg-white border border-border px-6 py-4 shadow-[0_20px_40px_rgba(46,33,21,0.08)]">
                  <div className="flex items-center gap-1 text-accent">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={14} fill="currentColor" strokeWidth={0} />
                    ))}
                  </div>
                  <p className="mt-1 text-sm font-medium">5,0 — 88 avis Planity</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Ribbon />

      {/* ── Bento : deux univers ─────────────────────────── */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-24 md:py-32" data-testid="home-univers">
        <Reveal>
          <Overline>Deux univers, une Maison</Overline>
          <h2 className="mt-6 font-display font-light tracking-tight text-4xl md:text-5xl max-w-xl leading-tight">
            Un studio d'exception, une école d'exigence.
          </h2>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-12 gap-5">
          {/* Le Studio — large */}
          <Reveal className="md:col-span-8">
            <Link
              to="/studio"
              data-testid="bento-studio-card"
              className="group relative block h-[380px] md:h-[440px] overflow-hidden border border-border"
            >
              <img
                src={IMAGES.resultsMacro}
                alt="Nanoblading — résultat poil à poil"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-foreground/35 transition-colors duration-500 group-hover:bg-foreground/45" />
              <div className="absolute inset-0 p-8 md:p-10 flex flex-col justify-end text-white">
                <Overline className="text-white/70">Le Studio — Paris 17ᵉ</Overline>
                <h3 className="mt-3 font-display text-3xl md:text-4xl font-light">La dermopigmentation d'exception</h3>
                <p className="mt-2 max-w-md text-white/80 font-light">
                  Nanoblading, hairstroke, lip blush, tricopigmentation : chaque trait est dessiné à la main, en studio privé.
                </p>
                <span className="mt-5 text-xs tracking-[0.2em] uppercase flex items-center gap-2">
                  Réserver un rendez-vous
                  <ArrowRight size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </Reveal>

          {/* L'Académie */}
          <Reveal delay={0.1} className="md:col-span-4">
            <Link
              to="/academie"
              data-testid="bento-academie-card"
              className="group relative block h-[380px] md:h-[440px] overflow-hidden border border-border"
            >
              <img
                src={IMAGES.academy}
                alt="L'Académie — formations Maison Ria"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-foreground/30 transition-colors duration-500 group-hover:bg-foreground/40" />
              <div className="absolute inset-0 p-8 flex flex-col justify-end text-white">
                <Overline className="text-white/70">L'Académie</Overline>
                <h3 className="mt-3 font-display text-3xl font-light">Transmettre le geste</h3>
                <span className="mt-4 text-xs tracking-[0.2em] uppercase flex items-center gap-2">
                  Découvrir les formations
                  <ArrowRight size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </Reveal>

          {/* Le Cercle Fidélité */}
          <Reveal delay={0.05} className="md:col-span-4">
            <div
              data-testid="bento-fidelite-card"
              className="relative block h-[320px] md:h-[360px] overflow-hidden border border-border"
            >
              <img
                src={IMAGES.fidelite}
                alt="Maison Ria — le rendu signature"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-foreground/30" />
              <div className="absolute inset-0 p-8 flex flex-col justify-end text-white">
                <Overline className="text-white/70">Le Cercle Fidélité</Overline>
                <h3 className="mt-3 font-display text-3xl font-light leading-snug">
                  Votre 8ᵉ visite, un privilège unique.
                </h3>
              </div>
            </div>
          </Reveal>

          {/* Le Diagnostic */}
          <Reveal delay={0.15} className="md:col-span-8">
            <Link
              to="/studio"
              data-testid="bento-diagnostic-card"
              className="group relative block h-[320px] md:h-[360px] overflow-hidden border border-border bg-primary text-primary-foreground"
            >
              <div className="absolute inset-0 p-8 md:p-10 flex flex-col justify-between">
                <div>
                  <Overline className="text-primary-foreground/60">Pour commencer</Overline>
                  <h3 className="mt-3 font-display text-3xl md:text-4xl font-light leading-snug max-w-md">
                    Le Diagnostic Sur-Mesure
                  </h3>
                  <p className="mt-4 max-w-md text-primary-foreground/70 font-light">
                    30 minutes pour analyser votre morphologie, votre peau et vos
                    attentes — et définir ensemble la technique idéale. 30 € au
                    studio ; l'échange à distance, par écrit, reste offert.
                  </p>
                </div>
                <span className="text-xs tracking-[0.2em] uppercase flex items-center gap-2">
                  Réserver le diagnostic
                  <ArrowRight size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ── Chiffres ─────────────────────────────────────── */}
      <section className="border-y border-border">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-16 grid grid-cols-2 lg:grid-cols-4 gap-10" data-testid="home-stats">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <p className="font-display text-4xl md:text-5xl font-light">{s.value}</p>
              <p className="mt-2 text-sm text-muted-foreground tracking-wide">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── La Maison ────────────────────────────────────── */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-24 md:py-32 grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-5">
          <div className="overflow-hidden border border-border">
            <img
              src={IMAGES.portraitLogo}
              alt="Oriane — fondatrice de Maison Ria"
              className="w-full aspect-[3/4] object-cover object-top transition-transform duration-700 hover:scale-105"
            />
          </div>
        </Reveal>
        <div className="lg:col-span-7 lg:pl-8">
          <Reveal>
            <Overline>La Maison</Overline>
            <h2 className="mt-6 font-display font-light tracking-tight text-4xl md:text-5xl leading-tight">
              Sublimer, sans transformer.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-base md:text-lg font-light leading-relaxed text-muted-foreground max-w-xl">
              Issue du milieu médical, Oriane a fondé Maison Ria avec une
              conviction : révéler la confiance en soi grâce à une beauté que
              personne ne remarque — sauf vous. Formée au sein des plus grands
              studios, elle a fait de l'ultra-naturel sa signature, des sourcils
              au cuir chevelu.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <ul className="mt-8 space-y-4 max-w-xl">
              {[
                "Issue du milieu médical",
                "Formée au sein des plus grands studios de dermopigmentation",
                "Pigments conformes au règlement européen REACH",
                "Un rendu ultra-naturel, imperceptible",
              ].map((item) => (
                <li key={item} className="flex items-start gap-4 border-b border-border pb-4">
                  <span className="w-1.5 h-1.5 rotate-45 bg-accent mt-2 shrink-0" />
                  <span className="font-light">{item}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.3}>
            <a
              data-testid="maison-planity-btn"
              href={PLANITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-10 inline-block bg-primary text-primary-foreground text-sm tracking-[0.14em] uppercase px-8 py-4 transition-colors duration-300 hover:bg-foreground"
            >
              Prendre rendez-vous
            </a>
          </Reveal>
        </div>
      </section>

      {/* ── Avis ─────────────────────────────────────────── */}
      <section className="bg-secondary/60 overflow-hidden">
        <div className="py-24 md:py-32" data-testid="home-avis">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12">
            <Reveal>
              <Overline>Elles en parlent</Overline>
              <h2 className="mt-6 font-display font-light tracking-tight text-4xl md:text-5xl">
                La confiance se lit sur les visages.
              </h2>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="mt-14">
            <div data-testid="avis-marquee">
              <Marquee speed={30} gradient={false} pauseOnHover>
                {marqueeLoop.map((item, i) =>
                  item.type === "avis" ? (
                    <figure
                      key={`avis-${i}`}
                      data-testid={`avis-card-${i}`}
                      className="w-[380px] md:w-[440px] mr-5 bg-white border border-border p-8 flex flex-col whitespace-normal"
                    >
                      <div className="flex items-center gap-1 text-accent">
                        {[...Array(5)].map((_, j) => (
                          <Star key={j} size={14} fill="currentColor" strokeWidth={0} />
                        ))}
                      </div>
                      <blockquote className="mt-5 font-display text-lg font-light leading-relaxed flex-1">
                        « {item.text} »
                      </blockquote>
                      <figcaption className="mt-6 text-sm text-muted-foreground">
                        <span className="text-foreground font-medium">{item.name}</span>
                        {item.location ? ` — ${item.location}` : ""} · {item.source}
                      </figcaption>
                    </figure>
                  ) : (
                    <div
                      key={`score-${i}`}
                      data-testid={`score-card-${i}`}
                      className="w-[260px] mr-5 bg-primary text-primary-foreground border border-primary p-8 flex flex-col justify-between whitespace-normal"
                    >
                      <p className="font-display text-5xl font-light">{item.value}</p>
                      <div className="mt-6">
                        <p className="text-sm font-medium">{item.label}</p>
                        <p className="mt-1 text-xs text-primary-foreground/60 tracking-[0.14em] uppercase">
                          Note moyenne Planity
                        </p>
                      </div>
                    </div>
                  )
                )}
              </Marquee>
            </div>
          </Reveal>

          <div className="mx-auto max-w-[1400px] px-6 md:px-12">
            <Reveal delay={0.15} className="mt-12">
              <div className="border border-border bg-background p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-8 justify-between">
                <div className="flex items-center gap-6">
                  <p className="font-display text-6xl font-light">5,0</p>
                  <p className="text-sm text-muted-foreground leading-relaxed max-w-[220px]">
                    La note moyenne de la Maison, sur toutes les plateformes.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    data-testid="avis-planity-link"
                    href={PLANITY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center justify-between gap-6 border border-border px-6 py-4 text-sm transition-colors duration-300 hover:bg-foreground hover:text-background"
                  >
                    88 avis sur Planity
                    <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </a>
                  <a
                    data-testid="avis-google-link"
                    href={GOOGLE_REVIEWS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center justify-between gap-6 border border-border px-6 py-4 text-sm transition-colors duration-300 hover:bg-foreground hover:text-background"
                  >
                    Avis Google
                    <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── CTA final ────────────────────────────────────── */}
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-24 md:py-32 text-left" data-testid="home-cta-final">
          <Reveal>
            <Overline className="text-primary-foreground/60">Maison Ria</Overline>
            <h2 className="mt-6 font-display font-light tracking-tight text-4xl md:text-6xl leading-tight max-w-3xl">
              Prête pour des sourcils qui vous{" "}
              <span className="font-script text-[1.05em]">ressemblent</span> ?
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="mt-10 flex flex-wrap gap-4">
            <button
              data-testid="cta-reserver-btn"
              onClick={() => navigate("/studio#reservation")}
              className="bg-primary-foreground text-primary text-sm tracking-[0.14em] uppercase px-8 py-4 transition-colors duration-300 hover:bg-accent"
            >
              Réserver au studio
            </button>
            <Link
              data-testid="cta-academie-btn"
              to="/academie"
              className="border border-primary-foreground/40 text-sm tracking-[0.14em] uppercase px-8 py-4 transition-colors duration-300 hover:border-primary-foreground"
            >
              Découvrir l'académie
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
